import React, { useEffect, useState } from "react";
import { IconCancelBlue, IconKeyboardArrowRightBlue } from "assets/svg";
import { Button, Image, message } from "antd";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { Product, Transaction, TransactionParams } from "common/interfaces";
import {
  fetchTransactionDetail,
  transactionCleanData,
  updateTransaction,
} from "reducers/transactionSlice";
import { fetchProductDetail, productCleanData } from "reducers/productSlice";
import { getLabelFromValue, productPrice } from "common/utils";
import { SHIP_CHARGE_TYPE } from "common/constants";
import { FILE_BASE_URL } from "config/apiConfig";
import { TransactionStatusEnum } from "common/enums/TransactionStatus.enum";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import Chat from "components/Chat";
import CustomConfirmModal from "components/CustomConfirmModal";
import { useCommonContext } from "contexts/CommonContext";

const BUYER_CANCEL_REQUEST_ALLOWED_STATUSES = [
  TransactionStatusEnum.REQUEST,
  TransactionStatusEnum.ACCEPT,
  TransactionStatusEnum.DELIVERED_TO_SHIP,
] as const;

const TransactionDetail = () => {
  const { setHeaderTitle, setHeaderTitleMobile } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const {
    transactionDetailData,
    updateTransactionData,
    error: transactionErr,
  } = useSelector((state: RootState) => state.transaction);
  const { productDetailData, error: productErr } = useSelector(
    (state: RootState) => state.product,
  );
  const productDetailInfo: Product | undefined = productDetailData?.data;
  const transactionDetailInfo: Transaction | undefined =
    transactionDetailData?.data;
  const controller = new AbortController();
  const { code, buyerId, sellerId } = useParams();
  const [openConfirmModalCancel, setOpenConfirmModalCancel] = useState(false);
  const [openConfirmModalApprove, setOpenConfirmModalApprove] = useState(false);

  const transactionParams: TransactionParams = {
    product_id: Number(productDetailInfo?.id),
    buyer_id: Number(buyerId),
    seller_id: Number(sellerId),
  };

  const cancel = () => {
    dispatch(
      updateTransaction({
        ...transactionParams,
        status: TransactionStatusEnum.BUYER_REQUEST_CANCEL,
      }),
    );
    setOpenConfirmModalCancel(false);
  };

  const approve = () => {
    dispatch(
      updateTransaction({
        ...transactionParams,
        status: TransactionStatusEnum.BUYER_ACCEPT_CANCEL,
      }),
    );
    setOpenConfirmModalApprove(false);
  };

  const displayStatus = () => {
    if (transactionDetailInfo?.status === TransactionStatusEnum.NEGOTIATE) {
      return "この商品は出品中です。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.REQUEST
    ) {
      return "この商品は購入申請中です。";
    } else if (transactionDetailInfo?.status === TransactionStatusEnum.ACCEPT) {
      return "この商品は受け取り待ちです。";
    } else if (transactionDetailInfo?.status === TransactionStatusEnum.REJECT) {
      return "取引がキャンセルされました。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.DELIVERED_TO_SHIP
    ) {
      return "この商品は受け取り待ちです。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.RECEIVED
    ) {
      return "この商品は受け取りが完了しました。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.INVOICE_ISSUED
    ) {
      return "請求書が発行されました。";
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.COMPLETED
    ) {
      return "取引が完了しました。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.BUYER_REQUEST_CANCEL
    ) {
      return "キャンセル申請中です。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.BUYER_ACCEPT_CANCEL
    ) {
      return "取引がキャンセルされました。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.SELLER_REQUEST_CANCEL
    ) {
      return "売り手企業からキャンセル申請が届きました。";
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.SELLER_ACCEPT_CANCEL
    ) {
      return "取引がキャンセルされました。";
    }
  };

  const displaySubmit = () => {
    if (
      transactionDetailInfo?.status === TransactionStatusEnum.DELIVERED_TO_SHIP
    ) {
      return (
        <Button
          block
          type="primary"
          htmlType="button"
          className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
          onClick={() =>
            dispatch(
              updateTransaction({
                ...transactionParams,
                status: TransactionStatusEnum.RECEIVED,
              }),
            )
          }
        >
          受け取り完了する
        </Button>
      );
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.INVOICE_ISSUED
    ) {
      return (
        <Link
          to={`${FILE_BASE_URL}/${transactionDetailData?.data.invoice_url}`}
          target="_blank"
        >
          <Button
            block
            type="primary"
            htmlType="button"
            className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
          >
            請求書をダウンロードする
          </Button>
        </Link>
      );
    } else if (
      transactionDetailInfo?.status ===
      TransactionStatusEnum.SELLER_REQUEST_CANCEL
    ) {
      return (
        <>
          <Button
            block
            type="primary"
            htmlType="button"
            className="w-full md:!w-[323px] min-h-16 bg-red-dark text-white text-[18px] md:text-xl font-bold"
            onClick={() => setOpenConfirmModalApprove(true)}
          >
            キャンセル申請を承認する
          </Button>
          <div className="self-stretch border-t border-white flex flex-col justify-start items-center gap-2.5">
            <div
              data-プロパティ1="base + icon"
              className="inline-flex justify-start items-center gap-[3px] cursor-pointer"
            >
              <IconCancelBlue className="w-[20px] h-[20px] aspect-[1/1]" />
              <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                キャンセル申請を承認しない
              </div>
            </div>
          </div>
        </>
      );
    } else if (
      transactionDetailInfo?.status === TransactionStatusEnum.NEGOTIATE
    ) {
      return (
        <Button
          block
          type="primary"
          htmlType="button"
          className="w-full md:!w-[323px] min-h-16 bg-primary text-white text-[18px] md:text-xl font-bold"
          onClick={() =>
            dispatch(
              updateTransaction({
                ...transactionParams,
                status: TransactionStatusEnum.REQUEST,
              }),
            )
          }
        >
          購入申請をする
        </Button>
      );
    }
  };

  const displayCancel = () => {
    if (
      BUYER_CANCEL_REQUEST_ALLOWED_STATUSES.some(
        (status) => status === transactionDetailInfo?.status,
      )
    ) {
      return (
        <div className="self-stretch border-t border-white flex flex-col justify-start items-center gap-2.5">
          <div
            data-プロパティ1="base + icon"
            className="inline-flex justify-start items-center gap-[3px] cursor-pointer"
            onClick={() => setOpenConfirmModalCancel(true)}
          >
            <IconCancelBlue className="w-[20px] h-[20px] aspect-[1/1]" />
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              この取引をキャンセルする
            </div>
          </div>
        </div>
      );
    }
  };

  useEffect(() => {
    if (productDetailInfo?.id && buyerId && sellerId) {
      dispatch(fetchTransactionDetail(transactionParams));
    }
  }, [productDetailInfo?.id, buyerId, sellerId, updateTransactionData]);

  useEffect(() => {
    if (code) {
      dispatch(fetchProductDetail(code, controller.signal));
    }
  }, [code]);

  useEffect(() => {
    if (transactionErr) {
      message.error(transactionErr);
    }
  }, [transactionErr]);

  useEffect(() => {
    if (productErr) {
      message.error(productErr);
    }
  }, [productErr]);

  useEffect(() => {
    setHeaderTitle(
      `${productDetailInfo?.product_name}（出品ID #${productDetailInfo?.code}）`,
    );
    setHeaderTitleMobile(`${productDetailInfo?.product_name}`);
    return () => {
      setHeaderTitleMobile("");
    };
  }, [productDetailInfo, setHeaderTitle, setHeaderTitleMobile]);

  useEffect(() => {
    return () => {
      dispatch(transactionCleanData());
      dispatch(productCleanData());
    };
  }, []);

  return (
    <div className="self-stretch w-full min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[40px] md:gap-10 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "商品検索",
            link: "/buyer/product/list",
          },
          {
            label: `${productDetailInfo?.product_name}（出品ID #${productDetailInfo?.code}）`,
            link: `/buyer/product/${productDetailInfo?.code}`,
          },
          {
            label: "取引チャット",
            isCurrent: true,
          },
        ]}
      />
      <div className="self-stretch flex flex-col justify-start items-start gap-12">
        <div className="self-stretch flex-col-reverse md:flex-row inline-flex justify-start items-start gap-12">
          <div
            data-プロパティ1="base"
            className="w-full md:w-72 inline-flex flex-col justify-center items-start"
          >
            <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
              <div className="w-16 h-16 p-2.5 bg-primary-light rounded-md inline-flex flex-col justify-center items-center gap-2.5">
                <Image
                  src={`${FILE_BASE_URL}/${productDetailInfo?.images[0]?.image_url}`}
                  className="!w-full !h-full object-cover"
                />
              </div>
              <div className="flex-1 justify-start text-black text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
                {productDetailInfo?.product_name}
              </div>
              <IconKeyboardArrowRightBlue className="w-[16px] h-[16px] aspect-[1/1]" />
            </div>
            <div className="self-stretch flex flex-col justify-start items-start">
              <div className="self-stretch py-3.5 bg-white border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  販売価格
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  {productPrice(productDetailInfo)}
                </div>
              </div>
              <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  送料
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  {getLabelFromValue(
                    SHIP_CHARGE_TYPE,
                    productDetailInfo?.shipping_charge_type,
                  )}
                </div>
              </div>
              <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  出品ID
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  #{productDetailInfo?.id}
                </div>
              </div>
              <div className="self-stretch py-3.5 bg-white border-t border-b border-neutral-border inline-flex justify-start items-center gap-3.5">
                <div className="w-16 justify-start text-black text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                  取引ID
                </div>
                <div className="flex-1 justify-start text-black text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                  #{transactionDetailInfo?.id}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full flex-1 inline-flex flex-col justify-start items-start gap-10">
            <div className="self-stretch p-7 bg-primary-light rounded-xl flex flex-col justify-center items-center gap-7">
              <div className="self-stretch px-5 py-7 bg-white rounded-md flex flex-col justify-start items-center gap-5">
                <div className="self-stretch flex-col md:flex-row inline-flex justify-center items-center gap-3">
                  <div
                    data-プロパティ1="border - bold"
                    className="min-w-14 px-2.5 py-[5px] bg-white rounded-md outline outline-2 outline-offset-[-2px] outline-primary inline-flex flex-col justify-center items-center gap-2.5"
                  >
                    <div className="text-center justify-start text-primary text-[11px] md:text-xs font-bold font-['Noto_Sans_JP'] leading-tight">
                      ステータス
                    </div>
                  </div>
                  <div className="justify-start text-black text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                    {displayStatus()}
                  </div>
                </div>
                {displaySubmit()}
              </div>
              <div className="w-full flex flex-col justify-center items-start gap-2.5">
                <div className="self-stretch inline-flex justify-start items-start">
                  <div className="w-24 justify-start text-black text-[13px] md:text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                    売り手企業
                  </div>
                  {transactionDetailInfo?.seller.code ? (
                    <Link
                      to={`/buyer/information?tab=profile&companyCode=${encodeURIComponent(
                        transactionDetailInfo.seller.code,
                      )}`}
                      className="flex-1 justify-start text-primary text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug hover:underline"
                    >
                      {transactionDetailInfo.seller.company_name}
                    </Link>
                  ) : (
                    <div className="flex-1 justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                      {transactionDetailInfo?.seller.company_name}
                    </div>
                  )}
                </div>
                <div className="self-stretch inline-flex justify-start items-start">
                  <div className="w-24 justify-start text-black text-[13px] md:text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                    所在地
                  </div>
                  <div className="flex-1 justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    {transactionDetailInfo?.seller.address}
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-start">
                  <div className="w-24 justify-start text-black text-[13px] md:text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
                    電話番号
                  </div>
                  <div className="flex-1 justify-start text-black text-[13px] md:text-sm font-normal font-['Noto_Sans_JP'] leading-snug">
                    {transactionDetailInfo?.seller.phone_number}
                  </div>
                </div>
              </div>
              {displayCancel()}
            </div>
            {buyerId && sellerId ? (
              <Chat
                buyerId={Number(buyerId)}
                sellerId={Number(sellerId)}
                productId={productDetailInfo?.id}
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModalCancel}
        title="この取引をキャンセル申請しますか？"
        description={`この取引のキャンセルを売り手企業に申請します。
          双方の合意によってキャンセルが成立するまで、
          取引は保留状態となります。`}
        note={`
          ※一度キャンセルを申請すると、取り消すことはできません。
          ※出品者の承認をもってキャンセルが成立します。`}
        onOk={cancel}
        onCancel={() => setOpenConfirmModalCancel(false)}
        okText="キャンセルを申請する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
      <CustomConfirmModal
        open={openConfirmModalApprove}
        title="キャンセル申請を承認しますか？"
        description={`売り手企業からのキャンセル申請を承認すると、
          この取引は完全にキャンセルとなります。`}
        note={`
          ※この操作は取り消すことができません。
          ※取引履歴には「キャンセル」として記録されます。`}
        onOk={approve}
        onCancel={() => setOpenConfirmModalApprove(false)}
        okText="キャンセルを承認する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
    </div>
  );
};

export default TransactionDetail;
